import React, { useMemo } from "react";
import {
  breakpoints,
  IconNames,
  Tag,
  TagVariant,
  useMediaQuery,
  useTheme
} from "@tvg/design-system";
import { useSelector } from "react-redux";
import { Handicapping, RunnerFlags, Timeform } from "@tvg/ts-types/Race";
import { getIsLogged } from "@urp/store-selectors";
import PickNumber from "../PickNumberAnalyse";
import { List } from "./styled-components";
import { getHandicappingTagsDescription } from "../../../../store/selectors";
import { FlagConfig, HandicappingRunnerFlags } from "./types";
import {
  isHandicappingFreePickTagShown,
  isTimeformFreePickTagShown
} from "../PickNumberAnalyse/functions";
import { PICKS_DATA_SOURCE } from "../InlineAnalyses/constants";

export type RunnerTag = {
  variant?: TagVariant;
  tags: RunnerFlags;
  qaLabel?: string;
  icon?: IconNames;
};

export interface RunnerTagsProps {
  data?: RunnerFlags;
  isScratched?: boolean;
  timeform?: Timeform;
  handicapping?: Handicapping;
  trackDataSource?: string;
  isInlineHandicapping?: boolean;
  isTotalOfTags?: boolean;
  hasSilk: boolean;
}

const defaultData = {
  horseInFocus: false,
  warningHorse: false,
  jockeyUplift: false,
  trainerUplift: false,
  horsesForCoursePos: false,
  horsesForCourseNeg: false,
  hotTrainer: false,
  coldTrainer: false,
  highestLastSpeedRating: false,
  sectionalFlag: false,
  significantImprover: false,
  jockeyInForm: false,
  clearTopRated: false,
  interestingJockeyBooking: false,
  firstTimeBlinkers: false,
  hasSilk: false
};

export const getNumberOfVisibleTags = (
  isMobile: boolean,
  isLogged: boolean,
  isTimeformTagShown: boolean,
  isHandicappingTagShown: boolean
) => {
  let numberOfOtherTags = 0;
  if (isTimeformTagShown && isHandicappingTagShown) {
    numberOfOtherTags = 2;
  } else if (isTimeformTagShown || isHandicappingTagShown) {
    numberOfOtherTags = 1;
  }

  return (isMobile ? 2 : 5) - (isLogged ? numberOfOtherTags : 0);
};

export const getLabel = (
  tag: string,
  runnerTagsDescription: HandicappingRunnerFlags
): string | null => {
  const tagValues = (runnerTagsDescription.flags || []).find(
    (tagItem: FlagConfig) => tag === tagItem.id
  );

  return tagValues ? tagValues.name : null;
};

const RaceTags = ({
  data = defaultData,
  isScratched,
  handicapping,
  timeform,
  trackDataSource,
  isInlineHandicapping,
  isTotalOfTags = false,
  hasSilk
}: RunnerTagsProps) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const runnerTagsDescription = useSelector(getHandicappingTagsDescription);
  const theme = useTheme();
  const isLogged = useSelector(getIsLogged);
  const isTimeformTagShown = isTimeformFreePickTagShown(
    timeform,
    PICKS_DATA_SOURCE.USGSA
  );
  const isHandicappingTagShown = isHandicappingFreePickTagShown(handicapping);

  const filteredTags = useMemo(
    () =>
      Object.keys(data).filter(
        (item) => !!data[item as keyof RunnerFlags] && item !== "__typename"
      ),
    [data]
  );

  const numberOfVisibleTags = getNumberOfVisibleTags(
    isMobile,
    isLogged,
    isTimeformTagShown || false,
    isHandicappingTagShown || false
  );
  const numberOfHiddenTags =
    filteredTags.length > numberOfVisibleTags
      ? filteredTags.length - numberOfVisibleTags
      : 0;

  const renderTagsList = useMemo(
    () =>
      filteredTags
        .slice(0, isTotalOfTags ? undefined : numberOfVisibleTags)
        .map((item: string) => {
          const label = getLabel(item, runnerTagsDescription);
          return (
            label && (
              <Tag
                key={label}
                size="m"
                variant="info"
                label={label}
                qaLabel="race-card-tag"
              />
            )
          );
        }),
    [filteredTags, numberOfVisibleTags, isTotalOfTags]
  );

  return filteredTags.length > 0 ||
    isTimeformTagShown ||
    isHandicappingTagShown ? (
    <List
      data-qa-label="race-card-tag-list"
      isScratched={isScratched}
      isInlineHandicapping={isInlineHandicapping}
      theme={theme}
      hasSilk={hasSilk}
    >
      {isLogged && (
        <PickNumber
          handicapping={handicapping}
          timeform={timeform}
          trackDataSource={trackDataSource}
        />
      )}
      {renderTagsList}
      {numberOfHiddenTags > 0 && !isTotalOfTags && (
        <Tag
          size="m"
          variant="info"
          label={`+${numberOfHiddenTags.toString()}`}
          qaLabel={`race-card-tag-plus-${numberOfHiddenTags.toString()}`}
        />
      )}
    </List>
  ) : null;
};

export default RaceTags;
